import isNil from 'lodash/isNil';
import {ON_ISSUE_VO_TITLE_MAP} from '../graphql/factory/OnIssue';
import {ItemType, Maybe, MetaInfoType} from '../graphql/generated/types';
import {t, tt} from './getTranslations';
import {OnIssueVo} from './shared/api/nicolas/vo/enums/OnIssueVo.enum';
import {ServicePropertyDto} from './shared/api/nicolas/vo/ServicePropertyDto.interface';
import {getFormattedNumberWithSuffix} from './shared/utils/getFormattedNumberWithSuffix';
import {getRatingText} from './util/getRatingText';

export function getSubtitleList({
  itemType,
  servicePropertyDto,
  authors,
  onIssueVo,
  metaInfoType,
  subCategoryName,
  categoryName,
  isAltText,
}: {
  itemType: ItemType;
  servicePropertyDto: Maybe<ServicePropertyDto>;
  authors?: string;
  onIssueVo?: OnIssueVo;
  metaInfoType?: Maybe<MetaInfoType>;
  subCategoryName?: string;
  categoryName?: string;
  isAltText?: boolean;
}) {
  const viewCount = servicePropertyDto?.view_count;
  const recommendedReason = servicePropertyDto?.recommend_reason;
  const ratingSum = servicePropertyDto?.rating_sum;
  const ratingCount = servicePropertyDto?.rating_count;

  const metaInfoTypeMap: {
    [key in MetaInfoType]: () => {
      categoryName?: string;
      subCategoryName?: string;
      authors?: string;
      onIssueVo?: OnIssueVo;
      viewCount?: number;
      recommendedReason?: string;
      ratingSum?: number;
      ratingCount?: number;
    };
  } = {
    [MetaInfoType.CategoryAndSubCategoryAndAuthorName]: () => ({
      categoryName,
      subCategoryName,
      authors,
    }),
    [MetaInfoType.SubCategoryAndViewCount]: () => ({
      subCategoryName,
      viewCount,
    }),
    [MetaInfoType.SubCategory]: () => ({
      subCategoryName,
    }),
    [MetaInfoType.CategoryAndAuthor]: () => ({
      categoryName,
      authors,
    }),
    [MetaInfoType.CategoryAndViewCount]: () => ({
      categoryName,
      viewCount,
    }),
    [MetaInfoType.Category]: () => ({
      categoryName,
    }),
    [MetaInfoType.RecommendReason]: () => ({
      recommendedReason,
    }),
    [MetaInfoType.ViewCount]: () => ({
      viewCount,
    }),
    [MetaInfoType.IssueStatus]: () => ({
      onIssueVo,
    }),
    [MetaInfoType.AuthorName]: () => ({
      authors,
    }),
    [MetaInfoType.Rating]: () => ({
      ratingSum,
      ratingCount,
    }),
    [MetaInfoType.None]: () => ({}),
  };

  const {
    categoryName: _categoryName = undefined,
    subCategoryName: _subCategoryName = undefined,
    viewCount: _viewCount = undefined,
    onIssueVo: _onIssueVo = undefined,
    authors: _authors = undefined,
    recommendedReason: _recommendedReason = undefined,
    ratingSum: _ratingSum = undefined,
    ratingCount: _ratingCount = undefined,
  } = metaInfoType
    ? metaInfoTypeMap[metaInfoType]()
    : itemType === ItemType.CardView
      ? {viewCount}
      : {viewCount, onIssueVo, authors};

  return [
    ...(_categoryName ? [_categoryName] : []),
    ...(_subCategoryName ? [_subCategoryName] : []),
    ...(_viewCount !== undefined
      ? [
          isAltText
            ? t('common_accessibility_view_count', {
                values: {
                  value: getFormattedNumberWithSuffix(_viewCount),
                },
              })
            : getFormattedNumberWithSuffix(_viewCount),
        ]
      : []),
    ...(_onIssueVo ? [tt(ON_ISSUE_VO_TITLE_MAP[_onIssueVo])] : []),
    ...(_authors
      ? [
          isAltText
            ? t('common_accessibility_authors', {
                values: {value: _authors},
              })
            : _authors,
        ]
      : []),
    ...(_recommendedReason ? [_recommendedReason] : []),
    ...(!isNil(_ratingSum) && !isNil(_ratingCount)
      ? [
          getRatingText({
            ratingSum: _ratingSum,
            ratingCount: _ratingCount,
          }),
        ]
      : []),
  ];
}
